import { useCallback, useState } from 'react';

import Stack from '@mui/material/Stack';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { FilesUpload } from '../../components/FileExplorer/components/FilesUpload';
import { IdSelect } from '../../components/IdSelect';
import { LimitedFeature } from '../../components/LimitedFeature';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { MissionPage_OneMission_QueryDocument } from '../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { TUS_CATEGORY_MISSION_FILES } from '../../pages/MissionFiles/constants/tus.constant';
import type { IdOption } from '../../components/IdSelect';

const QUERY_DOCUMENT_NAMES_TO_INVALIDATE = [
  getDocumentName(MissionPage_OneMission_QueryDocument),
];

export const QuotationProofFileFields_Mission_Fragment = graphql(`
  fragment QuotationProofFileFields_Mission_Fragment on Mission {
    fileDirectory {
      _id
      allowedFileCategories {
        _id
      }
    }
  }
`);

interface Props {
  readonly defaultFileCategoryId?: Id | null;
  readonly missionId: Id;
  readonly onUploadSuccess: () => void;
}

export const QuotationProofFileFields = ({
  defaultFileCategoryId,
  missionId,
  onUploadSuccess,
}: Readonly<Props>) => {
  const { missionFileCategories } = useReferencesContext();
  const { mission: rawMission } = useMissionReadyContext();
  const { fileDirectory } = getFragmentData(
    QuotationProofFileFields_Mission_Fragment,
    rawMission,
  );

  const allowedCategoryIds = fileDirectory.allowedFileCategories.map(
    (category) => category._id,
  );
  const isDefaultCategoryAllowed =
    !!defaultFileCategoryId &&
    allowedCategoryIds.includes(defaultFileCategoryId);

  const [selectedCategoryId, setSelectedCategoryId] = useState<
    Id | typeof EMPTY_STRING
  >(isDefaultCategoryAllowed ? defaultFileCategoryId : EMPTY_STRING);

  const handleCategoryIdChange = useCallback((id: Id | typeof EMPTY_STRING) => {
    setSelectedCategoryId(id);
  }, []);

  const categoryOptions: readonly IdOption<Id>[] = missionFileCategories
    .filter((category) => !category.hideInFileExplorer)
    .filter((category) => allowedCategoryIds.includes(category._id))
    .map((category) => ({ id: category._id, label: category.label }));

  const selectedCategory = missionFileCategories.find(
    (category) => category._id === selectedCategoryId,
  );

  return (
    <Stack spacing={2}>
      <IdSelect<Id, IdOption<Id>>
        disabled={!isDefaultCategoryAllowed}
        id="quotation-proof-file-dialog-file-category-id"
        label="Catégorie de fichier"
        onChange={handleCategoryIdChange}
        options={categoryOptions}
        value={selectedCategoryId}
      />
      {!isDefaultCategoryAllowed && <LimitedFeature />}
      {!!selectedCategoryId && (
        <FilesUpload
          entityId={missionId}
          entityLabel="Mission"
          fileCategoryId={selectedCategoryId}
          fileCategoryLabel={selectedCategory?.label}
          onUploadSuccess={onUploadSuccess}
          queryDocumentNamesToInvalidate={QUERY_DOCUMENT_NAMES_TO_INVALIDATE}
          tusCategory={TUS_CATEGORY_MISSION_FILES}
        />
      )}
    </Stack>
  );
};
